
import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { UpgradeAvailabilityDTO } from '../../../model/upgradeAvailabilityDTO';
import { AttentionFlashService } from '../../../_services/attention-flash.service';
import { ShoppingCartService } from '../../../_services/shopping-cart.service';
import { SharedService } from '../../../_services/shared.service';
import { Subscription } from 'rxjs';
import { OfferCategory, OFFER_CATEGORY_LABELS } from '../../../model/offer-category.model';
import { OfferCategoryService } from '../../../_services/offer-category.service';

export type UiNewOfferTab = OfferCategory;

type TabDef = { key: UiNewOfferTab; label: string; enabled: boolean };

@Component({
  selector: 'ui-new-offer-nav-bar',
  standalone: true,
  imports: [],
  templateUrl: './ui-new-offer-nav-bar.component.html',
  styleUrl: './ui-new-offer-nav-bar.component.scss',
})
export class UiNewOfferNavBarComponent {
  @Input() upgradeAvailability: UpgradeAvailabilityDTO | null = null;
  @Input() selectedTab: UiNewOfferTab = 'ROOM';

  @Output() selectedTabChange = new EventEmitter<UiNewOfferTab>();

  private readonly attentionFlash = inject(AttentionFlashService);
  private readonly shoppingCartService = inject(ShoppingCartService);
  private readonly sharedService = inject(SharedService);
  private readonly offerCategoryService = inject(OfferCategoryService);
  private readonly destroyRef = inject(DestroyRef);
  private flashSub?: Subscription;

  private flashingTabs = new Set<UiNewOfferTab>();

  private isTabAllowedByAppConfig(tab: UiNewOfferTab): boolean {
    const cfg = this.sharedService.applicationConfig?.prestay?.active_offer_types;
    // If config isn't available yet (or API doesn't provide it), don't block UI.
    if (!cfg) return true;

    if (tab === 'ROOM') return cfg.room !== false;
    if (tab === 'ECI') return cfg.eci !== false;
    if (tab === 'LCO') return cfg.lco !== false;
    if (tab === 'ANCILLARY') return cfg.addon !== false;
    return true;
  }

  get tabs(): TabDef[] {
    const u = this.upgradeAvailability;
    return [
      {
        key: 'ROOM',
        label: OFFER_CATEGORY_LABELS.ROOM,
        enabled: (this.isTabAllowedByAppConfig('ROOM') && (!!u?.room_available || u === null)),
      },
      {
        key: 'ECI',
        label: OFFER_CATEGORY_LABELS.ECI,
        enabled: this.isTabAllowedByAppConfig('ECI') && !!u?.eci_available,
      },
      {
        key: 'LCO',
        label: OFFER_CATEGORY_LABELS.LCO,
        enabled: this.isTabAllowedByAppConfig('LCO') && !!u?.lco_available,
      },
      {
        key: 'ANCILLARY',
        label: OFFER_CATEGORY_LABELS.ANCILLARY,
        enabled: this.isTabAllowedByAppConfig('ANCILLARY') && !!u?.addon_available,
      },
    ];
  }

  trackByTab(_index: number, tab: TabDef): UiNewOfferTab {
    return tab.key;
  }

  constructor() {
    this.flashSub = this.attentionFlash.flash$.subscribe(evt => {
      if (evt.target.kind !== 'nav-tab') return;
      const tab = evt.target.tab as UiNewOfferTab;
      if (evt.active) {
        this.flashingTabs.add(tab);
      } else {
        this.flashingTabs.delete(tab);
      }
    });

    this.destroyRef.onDestroy(() => this.flashSub?.unsubscribe());
  }

  isFlashing(tab: UiNewOfferTab): boolean {
    return this.flashingTabs.has(tab);
  }

  hasAnySelectedForTab(tab: UiNewOfferTab): boolean {
    return this.shoppingCartService.hasOffersInCartForTab(
      this.shoppingCartService.offerAliasId,
      this.offerCategoryService.normalizeCategory(tab)
    );
  }

  select(tab: UiNewOfferTab) {
    this.selectedTabChange.emit(tab);
  }
}
